import React, { useEffect, useState } from 'react';

// Host that should show the "you're on dev" popup, and where to point users for
// production. Kept as constants so the check is explicit and easy to update.
const DEV_HOST = 'lyric.dev.rapidai.com';
const PROD_URL = 'https://lyric.rapidai.com';
// Once per browser session: dismissing sets this in sessionStorage so the popup
// doesn't reappear on every client-side navigation, but a fresh tab/session
// shows it again.
const DISMISS_KEY = 'lyric.devBannerDismissed';

/**
 * Full-screen popup shown ONLY when the app is served from the dev host
 * (lyric.dev.rapidai.com), telling the user this is the development environment
 * and linking them to production. Renders nothing on any other host.
 */
const DevEnvBanner = () => {
  const isDevHost =
    typeof window !== 'undefined' &&
    window.location.hostname.toLowerCase() === DEV_HOST;

  const [dismissed, setDismissed] = useState(() => {
    if (!isDevHost) return true;
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const close = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* sessionStorage unavailable (private mode) — dismiss for this mount only */
    }
    setDismissed(true);
  };

  // Allow Esc to dismiss.
  useEffect(() => {
    if (!isDevHost || dismissed) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDevHost, dismissed]);

  if (!isDevHost || dismissed) return null;

  const styles = {
    backdrop: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.55)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 100000,
      padding: '16px',
    },
    card: {
      backgroundColor: '#fff',
      color: '#1a1a1a',
      maxWidth: '460px',
      width: '100%',
      borderRadius: '10px',
      boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
      borderTop: '6px solid #f0a020',
      padding: '24px 24px 20px',
      fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    },
    badge: {
      display: 'inline-block',
      backgroundColor: '#f0a020',
      color: '#1a1a1a',
      fontWeight: 700,
      fontSize: '11px',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      padding: '3px 8px',
      borderRadius: '4px',
      marginBottom: '12px',
    },
    title: {
      margin: '0 0 10px',
      fontSize: '20px',
      fontWeight: 700,
    },
    body: {
      margin: '0 0 20px',
      fontSize: '14px',
      lineHeight: 1.5,
      color: '#444',
    },
    host: {
      fontFamily: 'monospace',
      backgroundColor: '#f3f3f3',
      padding: '1px 5px',
      borderRadius: '4px',
      color: '#222',
    },
    actions: {
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
    },
    primary: {
      backgroundColor: '#0a66c2',
      color: '#fff',
      border: 'none',
      borderRadius: '6px',
      padding: '10px 16px',
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-block',
    },
    secondary: {
      backgroundColor: 'transparent',
      color: '#555',
      border: '1px solid #ccc',
      borderRadius: '6px',
      padding: '10px 16px',
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
    },
  };

  return (
    <div
      style={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dev-env-title"
      onClick={close}
    >
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        <span style={styles.badge}>Development</span>
        <h2 id="dev-env-title" style={styles.title}>
          You&rsquo;re on the Dev site
        </h2>
        <p style={styles.body}>
          This is the <strong>development</strong> environment (
          <span style={styles.host}>{DEV_HOST}</span>). It&rsquo;s used for
          testing and may be unstable or contain unreleased changes. For the
          production app, go to{' '}
          <span style={styles.host}>lyric.rapidai.com</span>.
        </p>
        <div style={styles.actions}>
          <button type="button" style={styles.secondary} onClick={close}>
            Continue on Dev
          </button>
          <a href={PROD_URL} style={styles.primary}>
            Go to Production &rarr;
          </a>
        </div>
      </div>
    </div>
  );
};

export default DevEnvBanner;
